import { FRAME, SAMPLE_RATE } from './vad';

/**
 * Elena's own ear on the microphone: 16 kHz, 16-bit mono audio in 20 ms frames, and the last
 * 15 seconds kept in memory (never sent anywhere by itself). That memory is what lets the
 * widget hand "Hej Elena, pusti David Guetta…" to a real recognizer after Chrome's free
 * recognizer only spotted the name: the words are already here.
 *
 * Shared by everyone who needs it (wake listening, the mic button, talk mode) and started
 * only while someone holds it.
 */

const BUFFER_SECONDS = 15;

/** Runs in the audio thread: any device rate in, 16 kHz Int16 frames out (linear resampling). */
const WORKLET = `
class ElenaTap extends AudioWorkletProcessor {
  constructor() {
    super();
    this.step = sampleRate / ${SAMPLE_RATE};
    this.pos = 0;
    this.prev = 0;
    this.out = new Int16Array(${FRAME});
    this.n = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    // pos walks the input at the input/output rate ratio; samples between are interpolated
    let pos = this.pos;
    while (pos < ch.length) {
      const i = Math.floor(pos);
      const frac = pos - i;
      const a = i === 0 ? this.prev : ch[i - 1];
      const b = ch[i];
      const s = a + (b - a) * frac;
      this.out[this.n++] = Math.max(-32768, Math.min(32767, Math.round(s * 32767)));
      if (this.n === ${FRAME}) {
        this.port.postMessage(this.out, [this.out.buffer]);
        this.out = new Int16Array(${FRAME});
        this.n = 0;
      }
      pos += this.step;
    }
    this.pos = pos - ch.length;
    this.prev = ch[ch.length - 1];
    return true;
  }
}
registerProcessor('elena-tap', ElenaTap);
`;

type FrameListener = (frame: Int16Array) => void;

/**
 * 16 kHz frames from any audio stream in this AudioContext: the mic here, the player's voice on
 * Elena's phone (#/caller). Returns the worklet node (disconnect it to stop).
 */
export async function tapStream(
  ctx: AudioContext,
  stream: MediaStream,
  onFrame: FrameListener,
): Promise<AudioWorkletNode> {
  const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }));
  try {
    await ctx.audioWorklet.addModule(url);
  } finally {
    URL.revokeObjectURL(url);
  }
  const node = new AudioWorkletNode(ctx, 'elena-tap', { numberOfOutputs: 0 });
  node.port.onmessage = (e: MessageEvent<Int16Array>) => onFrame(e.data);
  ctx.createMediaStreamSource(stream).connect(node);
  return node;
}

class MicTap {
  private holders = 0;
  private starting: Promise<boolean> | null = null;
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private readonly ring = new Int16Array(SAMPLE_RATE * BUFFER_SECONDS);
  private written = 0;
  private readonly listeners = new Set<FrameListener>();
  /** Why the mic could not start (shown in the widget). */
  error: string | null = null;

  get running(): boolean {
    return Boolean(this.node);
  }

  static supported(): boolean {
    return (
      typeof window !== 'undefined' &&
      Boolean(navigator.mediaDevices?.getUserMedia) &&
      typeof AudioWorkletNode !== 'undefined'
    );
  }

  /** Holds the mic open; resolves false when it can't be opened (no permission, no device). */
  acquire(): Promise<boolean> {
    this.holders++;
    if (this.node) return Promise.resolve(true);
    this.starting ??= this.start().finally(() => {
      this.starting = null;
    });
    return this.starting;
  }

  release(): void {
    this.holders = Math.max(0, this.holders - 1);
    if (this.holders === 0) this.stop();
  }

  private async start(): Promise<boolean> {
    if (!MicTap.supported()) {
      this.error = 'unsupported';
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        video: false,
      });
      const ctx = new AudioContext();
      const node = await tapStream(ctx, stream, (frame) => this.onFrame(frame));
      if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
      // released while the permission prompt was open
      if (this.holders === 0) {
        stream.getTracks().forEach((t) => t.stop());
        void ctx.close();
        return false;
      }
      this.stream = stream;
      this.ctx = ctx;
      this.node = node;
      this.error = null;
      return true;
    } catch (err) {
      this.error = (err as { name?: string })?.name ?? 'failed';
      return false;
    }
  }

  private stop(): void {
    this.node?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    void this.ctx?.close().catch(() => undefined);
    this.node = null;
    this.stream = null;
    this.ctx = null;
    this.written = 0;
  }

  private onFrame(frame: Int16Array): void {
    const size = this.ring.length;
    const at = this.written % size;
    const first = Math.min(frame.length, size - at);
    this.ring.set(frame.subarray(0, first), at);
    if (first < frame.length) this.ring.set(frame.subarray(first), 0);
    this.written += frame.length;
    this.listeners.forEach((l) => l(frame));
  }

  /** The last `seconds` of audio (as much as there is), oldest first. */
  recent(seconds = BUFFER_SECONDS): Int16Array {
    const size = this.ring.length;
    const n = Math.min(this.written, size, Math.round(seconds * SAMPLE_RATE));
    const out = new Int16Array(n);
    const start = (this.written - n) % size;
    const first = Math.min(n, size - start);
    out.set(this.ring.subarray(start, start + first), 0);
    if (first < n) out.set(this.ring.subarray(0, n - first), first);
    return out;
  }

  /** Every new 20 ms frame, until the returned function is called. */
  onAudio(listener: FrameListener): () => void {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  }
}

export const mic = new MicTap();
export const micSupported = (): boolean => MicTap.supported();
